import { count, kwh, timestamp } from "../../lib/format";
import { clockAgreement } from "../../lib/sessions";
import type { SessionDetail } from "../../lib/types";
import { Disclosure } from "../ui/Disclosure";
import { Datum, MicroLabel } from "../ui/Text";

/**
 * The evidence behind a session's figures, collapsed.
 *
 * Every field here answers "where did this number come from", which an auditor
 * eventually asks and an operator almost never does. So it is one click away, and
 * the closed summary carries the two facts that decide whether the click is worth
 * it: the registers the energy was computed from, and whether the station's clock
 * agreed with ours.
 */
export function SessionAudit({ row }: { readonly row: SessionDetail }) {
  const zone = row.depotTimezone;
  const clocks = clockAgreement(row);

  return (
    <Disclosure
      label="Audit trail"
      summary={
        <>
          {kwh(row.meterStartWh)} → {row.meterStopWh === null ? "…" : kwh(row.meterStopWh)} kWh
          {clocks === null ? null : (
            <>
              {" · "}
              <span className={clocks.isSuspect ? "text-signal" : ""}>{clocks.text}</span>
            </>
          )}
        </>
      }
    >
      <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
        {/* Spec 6.5's energy is stop − start and nothing else; all three side by
              side is what makes that checkable by hand. */}
        <Datum label="Opening register">{kwh(row.meterStartWh)} kWh</Datum>
        <Datum label="Closing register">
          {row.meterStopWh === null ? "not yet reported" : `${kwh(row.meterStopWh)} kWh`}
        </Datum>
        <Datum label="Readings">
          {row.sampleCount === null
            ? // Null until consolidation at close; "0" would claim the station sent nothing.
              row.state === "active"
              ? "still arriving"
              : "not consolidated yet"
            : count(row.sampleCount)}
        </Datum>
        <Datum label="Started (station)">{timestamp(row.startedAtCp, zone)}</Datum>
        <Datum label="Started (server)">{timestamp(row.startedAtServer, zone)}</Datum>
        <Datum label="Counts toward">
          {/* Spec 6.5: the station's end time, or the server's when its clock is
                suspect — which the flag above explains. */}
          {timestamp(row.attributedAt, zone)}
        </Datum>
        <Datum label="Ended (station)">{timestamp(row.endedAtCp, zone)}</Datum>
        <Datum label="Ended (server)">{timestamp(row.endedAtServer, zone)}</Datum>
        <Datum label="Consolidated">{timestamp(row.consolidatedAt, zone)}</Datum>
        <Datum label="Transaction id" mono>
          {row.ocppTransactionId}
        </Datum>
        <Datum label="Depot zone">{zone}</Datum>
        <Datum label="Samples held until">
          {row.retentionHoldUntil === null
            ? "standard retention"
            : timestamp(row.retentionHoldUntil, zone)}
        </Datum>
      </dl>

      {row.signedMeterData === null || row.signedMeterData === undefined ? null : (
        <div className="flex flex-col gap-2">
          <MicroLabel>Transaction data, verbatim</MicroLabel>
          <p className="m-0 max-w-prose text-data text-ink-muted">
            The StopTransaction's <code>transactionData</code> (spec 3.6). On a station with signed
            metering this is what a dispute rests on, so it is never normalised.
          </p>
          <pre className="m-0 max-h-80 overflow-auto border border-rule bg-paper-sunken p-3 text-micro tracking-normal normal-case text-ink">
            {JSON.stringify(row.signedMeterData, null, 2)}
          </pre>
        </div>
      )}
    </Disclosure>
  );
}
